import { Component, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationLoadingService } from '@core/services/navigation-loading.service';

// Total time (ms) the bar takes to fill 0 → 100% during navigation
const FILL_DURATION_MS = 700;
// Interval tick (ms)
const TICK_MS = 16; // ~60fps
// % increment per tick for steady fill
const STEP = (100 / FILL_DURATION_MS) * TICK_MS; // ~2.3% per tick

@Component({
  selector: 'app-navigation-loader',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible) {
      <div class="nav-loader">
        <div class="nav-loader-bar">
          <div
            class="nav-loader-progress"
            [style.width.%]="progress"
            [style.transition]="completing ? 'width 0.2s ease-out' : 'none'">
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .nav-loader {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 9999;
      height: 6px;
      background: rgba(0, 188, 212, 0.1);
      overflow: hidden;
      animation: fadeIn 0.3s ease-in-out;
    }

    .nav-loader-bar {
      position: relative;
      width: 100%;
      height: 100%;
      background: transparent;
      overflow: hidden;
    }

    .nav-loader-progress {
      height: 100%;
      background: #00BCD4;
      border-radius: 0 3px 3px 0;
      box-shadow: 0 0 8px rgba(0, 188, 212, 0.4);
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `]
})
export class NavigationLoaderComponent implements OnDestroy {
  navigationLoadingService = inject(NavigationLoadingService);

  visible = false;
  progress = 0;
  completing = false;

  private fillInterval?: ReturnType<typeof setInterval>;
  private pollingInterval?: ReturnType<typeof setInterval>;
  private isCurrentlyNavigating = false;

  constructor() {
    this.pollingInterval = setInterval(() => {
      const isNavigating = this.navigationLoadingService.isNavigating();
      if (isNavigating === this.isCurrentlyNavigating) return;

      this.isCurrentlyNavigating = isNavigating;
      if (isNavigating) {
        this.startFill();
      } else {
        this.finishFill();
      }
    }, 50);
  }

  ngOnDestroy(): void {
    if (this.fillInterval) clearInterval(this.fillInterval);
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  }

  private startFill(): void {
    this.completing = false;
    this.progress = 0;
    this.visible = true;

    if (this.fillInterval) clearInterval(this.fillInterval);

    // Steadily fill 0 → 100% over FILL_DURATION_MS
    this.fillInterval = setInterval(() => {
      this.progress = Math.min(100, this.progress + STEP);

      if (this.progress >= 100) {
        this.hideSoon();
      }
    }, TICK_MS);
  }

  private finishFill(): void {
    // Navigation done — rush remaining width to 100%
    if (this.fillInterval) {
      clearInterval(this.fillInterval);
      this.fillInterval = undefined;
    }
    this.completing = true;
    this.progress = 100;
    this.hideSoon();
  }

  private hideSoon(): void {
    if (this.fillInterval) {
      clearInterval(this.fillInterval);
      this.fillInterval = undefined;
    }
    window.setTimeout(() => {
      this.visible = false;
      this.progress = 0;
      this.completing = false;
    }, 250);
  }
}
